<template>
    <div @click="activateEdit" class="rich-text-editor">
        <!-- Vista previa (readonly) -->
        <div v-if="!isEditing" v-html="renderedHtml"
            :class="['cursor-text', editorClass, (currentCount <= 1 && !isEditing) ? 'show-text-placeholder' : 'show-text-content', readonly ? 'mode-readonly' : '']" />
        <!-- Editor activo -->
        <vue-editor v-else v-model="internalValue" @input="onInput" :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            :class="[editorClass, (currentCount <= 1 && isEditing) ? 'show-text-placeholder' : 'show-text-content']"
            @blur="deactivateEdit" />
        <!-- Contador y error -->
        <div :class="['maxSize', { 'over-limit': isOverLimit }]">
            <template v-if="limitType === 'words'">
                Words: {{ currentCount }} / {{ limit }}
            </template>
            <template v-else-if="limitType === 'lines'">
                Lines: {{ currentCount }} / {{ limit }}
            </template>
        </div>
        <div v-if="isOverLimit && isEditing" :class="['text-red-600 text-sm mt-1', errorClass]">
            Has alcanzado el límite de {{ limitType === 'words' ? 'palabras' : 'líneas' }} permitido.
        </div>
    </div>
</template>
<script>
import { VueEditor } from "vue2-editor";
import "./RichTextEditor.scss"
export default {
    name: "RichTextEditor",
    components: { VueEditor },
    props: {
        value: { type: String, default: "" },
        limit: { type: Number, default: 100 },
        limitType: {
            type: String,
            default: "words",
            validator: (v) => ["words", "lines"].includes(v),
        },
        placeholder: { type: String, default: "Haz clic para editar..." },
        editorClass: { type: String, default: "" },
        errorClass: { type: String, default: "" },
        emitOnLimitReached: { type: Boolean, default: false, required: false },
        readonly: { type: Boolean, default: false, required: false },
        toolbar: {
            required: false,
            type: Array,
            default: () => [
                ["bold", "italic", "underline", { color: [] }],
                [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                ["link", "clean"],
            ],
        },
    },
    data() {
        return {
            internalValue: this.value || "",
            isEditing: false,
            customToolbar: this.toolbar.length > 0 ? this.toolbar : [
                ["bold", "italic", "underline", { color: [] }],
                [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                ["link", "clean"],
            ],
            pendingExtra: null,
        };
    },
    computed: {
        plainText() {
            const div = document.createElement("div");
            div.innerHTML = this.internalValue || "";
            return div.textContent || div.innerText || "";
        },
        wordCount() {
            return this.plainText.trim().split(/\s+/).filter(Boolean).length;
        },
        lineCount() {
            return this.plainText.split(/\r\n|\r|\n/).length;
        },
        editorOptions() {
            return {
                placeholder: this.placeholder,
                readOnly: this.isOverLimit,
                // theme: 'bubble',
            };
        },
        renderedHtml() {
            if (!this.internalValue) return this.placeholder;
            // Si ya tiene HTML válido, lo respetamos
            const isHtml = /<\/?[a-z][\s\S]*>/i.test(this.internalValue);
            return isHtml
                ? this.internalValue
                : this.internalValue.replace(/\n/g, "<br>");
        },
        currentCount() {
            const safeValue = this.internalValue || "";
            if (this.limitType === "words") {
                return safeValue.trim().split(/\s+/).filter(w => w).length;
            }
            if (this.limitType === "lines") {
                const htmlBreaks = safeValue.match(/<\/p>|<\/div>|<br\s*\/?>/gi);
                const plainBreaks = safeValue.split(/\r\n|\r|\n/).length;
                return htmlBreaks ? htmlBreaks.length : plainBreaks;
            }
            return 0;
        },
        isOverLimit() {
            return this.limit > 0 && this.currentCount > this.limit;
        },
    },
    watch: {
        internalValue(newVal) {
            if (!this.isOverLimit) {
                this.emitResponseValue("input", newVal);
            }
        },
        value(newVal) {
            if (newVal !== this.internalValue) {
                this.internalValue = typeof newVal === "string" ? newVal : "";
            }
        },
    },
    methods: {
        limitContentByLines(html, maxLines) {
            if (!html) return "";
            const regex = /<[^>]*>|[^<]+/g;
            const tokens = html.match(regex) || [];
            let lineCount = 0;
            let limitedHtml = "";
            for (let i = 0; i < tokens.length && lineCount < maxLines; i++) {
                const token = tokens[i];
                limitedHtml += token;
                // Contar líneas como bloques de texto que terminan en </p>, </div>, o <br>
                if (
                    /<\/p\s*>/.test(token) ||
                    /<\/div\s*>/.test(token) ||
                    /<br\s*\/?>/.test(token)
                ) {
                    lineCount++;
                }
            }
            return limitedHtml;
        },
        // metodos que devuelven el valor al padre
        emitResponseValue(events, val) {
            if (!events) {
                this.$emit("input", val);
                return;
            }
            // Si solo es un evento como string
            if (typeof events === "string") {
                this.$emit(events, val);
                return;
            }
            // Si es un array de eventos
            if (Array.isArray(events)) {
                events.forEach(evt => {
                    if (typeof evt === "string") {
                        this.$emit(evt, val);
                    } else if (typeof evt === "object" && evt.name) {
                        this.$emit(evt.name, evt.payload !== undefined ? evt.payload : val);
                    }
                });
            }
        },
        onInput(val) {
            if (this.limitType === "lines" && this.limit > 0) {
                const currentLineCount = (val.match(/<\/p>|<\/div>|<br\s*\/?>/gi) || []).length;
                if (currentLineCount > this.limit) {
                    const trimmed = this.limitContentByLines(val, this.limit);
                    const fullTokens = val.match(/<[^>]*>|[^<]+/g) || [];
                    const trimmedTokens = trimmed.match(/<[^>]*>|[^<]+/g) || [];
                    const extraTokens = fullTokens.slice(trimmedTokens.length);
                    const extra = extraTokens.join("");
                    this.internalValue = trimmed;
                    this.emitResponseValue("input", trimmed)
                    if (this.emitOnLimitReached) {
                        this.pendingExtra = {
                            trimmed,
                            extra,
                            limitType: this.limitType,
                            limit: this.limit,
                            currentCount: this.currentCount,
                        };
                    }
                    return;
                }
            }
            if (this.limitType === "words" && this.limit > 0) {
                const words = val.trim().split(/\s+/).filter(Boolean);
                if (words.length > this.limit) {
                    const trimmedWords = words.slice(0, this.limit);
                    const excessWords = words.slice(this.limit);
                    const trimmed = trimmedWords.join(" ");
                    const extra = excessWords.join(" ");
                    this.internalValue = trimmed;
                    this.emitResponseValue("input", trimmed);
                    if (this.emitOnLimitReached) {
                        this.pendingExtra = {
                            trimmed,
                            extra,
                            limitType: this.limitType,
                            limit: this.limit,
                            currentCount: this.currentCount,
                        };
                    }
                    return;
                }
            }
            this.internalValue = val;
            this.emitResponseValue("input", this.internalValue);
        },
        activateEdit() {
            if (!this.isEditing && !this.readonly) {
                // Solo si no es HTML, lo transformamos con <p></p>
                const isHtml = /<\/?[a-z][\s\S]*>/i.test(this.internalValue);
                if (!isHtml) {
                    this.internalValue = (this.internalValue || "").toString()
                        .split("\n")
                        .map(line => `<p>${line.trim()}</p>`)
                        .join("");
                }
                this.isEditing = true;
                this.$nextTick(() => {
                    const editor = this.$el.querySelector(".ql-editor");
                    if (editor) editor.focus();
                });
            }
        },
        deactivateEdit() {
            setTimeout(() => {
                const activeEl = document.activeElement;
                const editorEl = this.$el.querySelector(".ql-editor");
                // Si el foco sigue en el editor o en un menú del toolbar, no salimos
                if (
                    this.$el.contains(activeEl) ||
                    (activeEl && activeEl.closest(".ql-toolbar")) ||
                    (activeEl && activeEl.closest(".ql-tooltip"))
                ) {
                    return;
                }
                // Limpieza de <p></p> vacíos
                this.internalValue = this.internalValue.replace(/<p>\s*<\/p>/g, "");
                this.emitResponseValue("change", this.internalValue);
                // Salir de edición
                this.isEditing = false;
                if (this.pendingExtra && this.emitOnLimitReached) {
                    // this.$emit("limit-reached", this.pendingExtra);
                    this.emitResponseValue('limit-reached', this.pendingExtra);
                    this.pendingExtra = null;
                }
            }, 90); // Pequeño delay para permitir que los menús se registren
        }
    },
};
</script>
<style scoped>
.maxSize {
    transition: color 0.3s ease, font-weight 0.3s ease;
}

.maxSize.over-limit {
    background-color: rgba(255, 0, 0, 0.05);
    border-radius: 4px;
    padding: 2px 4px;
}
</style>